import ErrorMessage from "components/common/ErrorMessage";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { Select } from "components/common/Select";
import ListTable from "components/dashboard/ListTable";
import { fetchDashboardAnalytics } from "slices/dashboardSlice";
import { fetchDashboardAppointments } from "slices/dashboardSlice";
import { DashboardSelector } from "slices/dashboardSlice";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { setError } from "slices/dashboardSlice";
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import FullPageLoader from "components/common/FullPageLoader";
import { usePermissions } from "layouts/Admin";
import { fetchFacilitiesDirect, fetchFacilities } from "slices/facilitiesSlice";
import { departmentsSelector } from "slices/departmentsSlice";
import { facilitiesSelector } from "slices/facilitiesSlice";
import { fetchDepartmentsDirect, fetchDepartments } from "slices/departmentsSlice";
import { LegendButton } from "components/common/TableColor";
import { legends } from "components/common/TableColor";
import { fetchVendors } from "slices/vendorSlice";


const DatesInputs = (({ control, errors }) => {
  const startDateRef = React.useRef(null);
  const endDateRef = React.useRef(null);

  return (
    <>
      <div className="row-primary">
        <h2>Start Date*</h2>
        <Controller
          name="start_date"
          control={control}
          render={({ field }) =>
            <div className="input-image basic_bases">
              <input ref={startDateRef} id='start_date' type="date" name='start_date' placeholder='Start Date'
                {...field}
                value={moment(field.value).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const dateString = moment(e.target.value).format('YYYY/MM/DD')
                  field.onChange(dateString)
                }}
              />
              <img src="/images/celender.png" alt="celender Icon" className="custom-icon" />

            </div>
          } />
      </div>
      <div className="row-primary">
        <h2>End Date*</h2>
        <Controller
          name="end_date"
          control={control}
          render={({ field }) =>
            <div className="input-image basic_bases">
              <input ref={endDateRef} type="date" name='end_date' placeholder='End Date'
                {...field}
                value={moment(field.value).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const dateString = moment(e.target.value).format('YYYY/MM/DD')
                  field.onChange(dateString)
                }}
              />
              <img src="/images/celender.png" alt="celender Icon" className="custom-icon" />
            </div>
          } />
      </div>
    </>
  )
});

function Dashboard() {
  const { checkPermission } = usePermissions()
  const dispatch = useDispatch()
  const { dashboard, last_page: totalPages, loader, total } = useSelector(DashboardSelector);
  const { language: languages } = useSelector(languageSelector);
  const { departments } = useSelector(departmentsSelector);
  const { facilities } = useSelector(facilitiesSelector);
  const defaultValues = {
    status: 'all',
    start_date: moment().format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    facility_id: '',
    vendor_id: '',
    language_id: '',
    department_id: '',
  };
  const { clearErrors, handleSubmit, watch, formState: { errors }, control, setValue, getValues, setError: setErrors, reset, } = useForm({
    defaultValues: defaultValues
  });
  const [activeLegend, setActiveLegend] = useState(0);
  const [statusValue, setStatusValue] = React.useState('all');
  const [filterKey, setFilterKey] = useState(0);

  const watchedValues = watch(['start_date', 'end_date', 'language_id', 'facility_id', 'department_id', 'vendor_id']);
  const [start_date, end_date, language_id, facility_id, department_id, vendor_id] = watchedValues;

  const paginationParams = React.useMemo(() => ({
    start_date: start_date ? moment(start_date).format('YYYY-MM-DD') : undefined,
    end_date: end_date ? moment(end_date).format('YYYY-MM-DD') : undefined,
    language_id,
    facility_id,
    department_id,
    vendor_id,
    status: statusValue !== 'all' ? statusValue : undefined
  }), [start_date, end_date, language_id, facility_id, department_id, vendor_id, statusValue]);

  useEffect(() => {
    dispatch(fetchDashboardAnalytics())
    dispatch(fetchDashboardAppointments(1, { pageLength: 10, ...defaultValues, sortBy: "id", sortDirection: "desc" }))
    dispatch(fetchFacilitiesDirect())
    dispatch(fetchDepartmentsDirect())
  }, [])


  // #region Language dropdown 
  const [selectedLanguage, setSelectedLanguage] = useState(null);

  const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true))
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion


  // #region Department dropdown
  const [filteredDepartments, setFilteredDepartments] = useState(null);

  const filteredDepartmentsLoadOptions = React.useCallback(async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchDepartments(
        page,
        {
          pageLength: 20,
          sortBy: "id",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );
    return {
      options: result?.data?.map((val) => ({
        value: val.id,
        label: val.department_name || val.name || val.label || val.abbreviation || "",
      })) || [],

      hasMore: page < Number(result?.last_page),
      additional: {
        page: page + 1,
      },
    };
  }, [dispatch]);
  // #endregion


  // #region Facility dropdown
  const [filteredFacilities, setFilteredFacilities] = useState(null);

  const filteredFacilitiesLoadOptions = React.useCallback(async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchFacilities(
        page,
        {
          pageLength: 20,
          sortBy: "name",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );
    return {
      options: result?.data?.map((val) => ({
        value: val.id,
        label: val.facility_name || val.abbreviation || val.name || val.label,
      })) || [],

      hasMore: page < Number(result?.last_page),
      additional: {
        page: page + 1,
      },
    };
  }, [dispatch]);

  // #endregion

  // #region Vendor dropdown
  const [filteredVendors, setFilteredVendors] = useState(null);

  const filteredVendorsLoadOptions = React.useCallback(async (
    inputValue,
    loadedOptions,
    { page }
  ) => {
    const result = await dispatch(
      fetchVendors(
        page,
        {
          peak: true,
          pageLength: 20,
          sortBy: "name",
          sortDirection: "asc",
          search: inputValue,
        },
        true,
        true
      )
    );
    return {
      options: result?.data?.map((val) => ({
        value: val.id,
        label: val.name || "",
      })) || [],

      hasMore: page < Number(result?.last_page),
      additional: {
        page: page + 1,
      },
    };
  }, [dispatch]);
  // #endregion

  // #region Legends 
  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...legends];
    temp_array.unshift({
      value: "all",
      name: "All",
      id: 0,
      color: "#3176A0"
    })
    return temp_array;
  }, [legends]);

  const hanldeActiveLegend = (id, value) => {
    setActiveLegend(id);
    const formValues = getValues();
    dispatch(fetchDashboardAppointments(1, { 
      pageLength: 10, 
      status: value, 
      start_date: moment(formValues.start_date).format('YYYY-MM-DD'), 
      end_date: moment(formValues.end_date).format('YYYY-MM-DD'), 
      sortBy: "id", 
      sortDirection: "desc",
      vendor_id: formValues.vendor_id,
      facility_id: formValues.facility_id,
      language_id: formValues.language_id,
      department_id: formValues.department_id,
    }))
    setStatusValue(value)
    setFilterKey(prev => prev + 1)
  }

  //#endregion

  const onSubmit = (data) => {
    dispatch(setError({}))
    const params = {
      ...data,
      status: statusValue,
      pageLength: 10,
      sortBy: "id",
      sortDirection: "desc"
    };
    dispatch(fetchDashboardAppointments(1, params))
    setFilterKey(prev => prev + 1)

    clearErrors();
  };

  const handleResetFields = () => {
    setValue("start_date", moment().format('YYYY-MM-DD'));
    setValue("end_date", moment().format('YYYY-MM-DD'));
    setValue("language_id", "");
    setSelectedLanguage(null);
    setValue("vendor_id", "");
    setFilteredVendors(null);
    setValue("department_id", "");
    setFilteredDepartments(null);
    setValue("facility_id", "");
    setFilteredFacilities(null);
    setStatusValue('all');
    setActiveLegend(0);
    dispatch(fetchDashboardAppointments(1, { ...defaultValues, sortBy: "id", sortDirection: "desc", pageLength: 10 }))
    setFilterKey(prev => prev + 1)
    clearErrors()
  };

  const [isFourteenDays, setIsFourteenDays] = useState(false);
  const tableRef = useRef(null);

  const handleFilter = (item, isforteen, statusFilter = 'all') => {
    const today = moment().format('YYYY-MM-DD');
    const tomorrow = moment().add(1, 'days').format('YYYY-MM-DD');
    const next_fourteen_days = moment().add(14, 'days').format('YYYY-MM-DD');
    if (isforteen == true) {
      setValue("start_date", tomorrow);
      setValue("end_date", next_fourteen_days);
    } else {
      setValue("start_date", today);
      setValue("end_date", today);
    }

    const matchedLegend = filteredStatuses.find(s => s.value === statusFilter);
    setActiveLegend(matchedLegend?.id ?? 0);
    setStatusValue(statusFilter);

    const formValues = getValues();
    const data = {
      ...formValues,
      pageLength: 10,
      start_date: isforteen ? tomorrow : today,
      end_date: isforteen ? next_fourteen_days : today,
      service: item?.value || "",
      status: statusFilter !== 'all' ? statusFilter : undefined,
      sortBy: "id",
      sortDirection: "desc",
    };

    dispatch(fetchDashboardAppointments(1, data));
    tableRef.current?.scrollIntoView({ behavior: "smooth" });
    dispatch(setError({}));
    clearErrors();
  };


  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">Dashboard</h2>
        </div>

        <div className="row_dash_1">
          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Today</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.todays_data?.map((service, index) => (
                    <tr key={index}>
                      <td style={{ cursor: 'pointer' }} onClick={() => { setIsFourteenDays(false); handleFilter(service, false, 'all'); }}>{service?.type}</td>
                      <td style={{ cursor: 'pointer' }} onClick={() => { setIsFourteenDays(false); handleFilter(service, false, 'registered'); }}>{service?.count}</td>
                      <td style={{ cursor: 'pointer' }} onClick={() => { setIsFourteenDays(false); handleFilter(service, false, 'open'); }}>{service?.to_cover}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Next 14 Days</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.fourteen_days?.map((service, index) => (
                    <tr key={index}>
                      <td style={{ cursor: 'pointer' }} onClick={() => { setIsFourteenDays(true); handleFilter(service, true, 'all'); }}>{service?.type}</td>
                      <td style={{ cursor: 'pointer' }} onClick={() => { setIsFourteenDays(true); handleFilter(service, true, 'registered'); }}>{service?.count}</td>
                      <td style={{ cursor: 'pointer' }} onClick={() => { setIsFourteenDays(true); handleFilter(service, true, 'open'); }}>{service?.to_cover}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>


        <div className="card_table">
          <div className="button-sm forLegends p-3">
            <LegendButton activeId={activeLegend} options={filteredStatuses} handleClick={hanldeActiveLegend} />
          </div>
          <form className="search_form_ad">
            <div className="row-primary dahboard_filter">
              <DatesInputs control={control} errors={errors} />
              <div className="row-primary">
                <h2>Vendor</h2>
                <Controller
                  name="vendor_id"
                  control={control}
                  render={({ field }) =>
                    <div className="basic_bases">
                      <AsyncPaginate
                        menuPlacement='auto'
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={filteredVendors}
                        loadOptions={filteredVendorsLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          setFilteredVendors(obj);
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  }
                />
              </div>
              <div className="row-primary">
                <h2>Facility</h2>
                <Controller
                  name="facility_id"
                  control={control}
                  render={({ field }) =>
                    <div className="basic_bases">
                      <div className="basic_bases">
                        <AsyncPaginate
                          menuPlacement='auto'
                          styles={{
                            ...selectCustomStyles,
                          }}
                          debounceTimeout={300}
                          value={filteredFacilities}
                          loadOptions={filteredFacilitiesLoadOptions}
                          isClearable
                          onChange={(obj) => {
                            setFilteredFacilities(obj);
                            field.onChange(obj?.value);
                          }}
                          additional={{
                            page: 1,
                          }}
                        />
                      </div>
                    </div>
                  }
                />
              </div>
              <div className="row-primary">
                <h2>Language</h2>
                <Controller
                  name="language_id"
                  control={control}
                  render={({ field }) =>
                    <div className="basic_bases">
                      <AsyncPaginate
                        menuPlacement='auto'
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={selectedLanguage}
                        loadOptions={filteredLanguagesLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          setSelectedLanguage(obj);
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  }
                />
              </div>


              <div className="row-primary">
                <h2>Department</h2>
                <Controller
                  name="department_id"
                  control={control}
                  render={({ field }) =>
                    <div className="basic_bases">
                      <AsyncPaginate
                        menuPlacement='auto'
                        styles={{
                          ...selectCustomStyles,
                        }}
                        debounceTimeout={300}
                        value={filteredDepartments}
                        loadOptions={filteredDepartmentsLoadOptions}
                        isClearable
                        onChange={(obj) => {
                          setFilteredDepartments(obj);
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  }
                />
              </div>
            </div>

            <div className="row-primary btns_area row_10">
              <SubmitButton handleSubmit={handleSubmit(onSubmit)} buttonType="button" btnText="Search" className={'green'} />
              <SubmitButton buttonType="button" className={'lightBlue'} btnText="Reset" handleSubmit={handleResetFields} />
            </div>

            <div ref={tableRef}>
              <ListTable data={dashboard?.appointments} paginationParams={paginationParams} filterKey={filterKey} />
            </div>

          </form>
        </div>

      </div>


      {loader && <FullPageLoader />}
    </>
  );
}

export default Dashboard;